import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Activity, Rss, Radar, CalendarClock, Eraser, RefreshCw, FileSearch } from "lucide-react";
import { BACKEND_URL } from "../../lib/backend";
import { HiddenReportsList } from "./HiddenReportsList";

const API = `${BACKEND_URL}/api`;
const errMsg = (e, fb) => e?.response?.data?.detail || e?.message || fb;
const ago = (iso) => {
  if (!iso) return "never";
  const h = Math.round((Date.now() - new Date(iso).getTime()) / 36e5);
  return h < 1 ? "just now" : h < 48 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
};

const Stat = ({ dm, label, value, warn, testid }) => (
  <div className="min-w-0">
    <p className={`category-tag mb-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{label}</p>
    <p className={`text-xl font-semibold ${warn ? "text-amber-600" : dm ? "text-slate-100" : "text-[#191c1d]"}`}
       style={{ fontFamily: "'Reckless Neue Variable', serif" }} data-testid={testid}>{value ?? "—"}</p>
  </div>
);

const RunLine = ({ dm, label, run, testid }) => (
  <p className={`text-[11px] ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`} data-testid={testid}>
    <span className={`font-medium ${dm ? "text-slate-300" : "text-[#191c1d]"}`}>{label}</span>{" "}
    {run ? `${ago(run.finished_at || run.started_at)} · ${run.reports_inserted ?? 0} new` : "never run"}
    {run?.sources_created?.length ? ` · ${run.sources_created.length} new source(s)` : ""}
    {run?.errors?.length ? ` · ${run.errors.length} error(s)` : ""}
  </p>
);

const OneShots = ({ dm, shots, src }) => {
  if (!shots) return null;
  const muted = dm ? "text-slate-500" : "text-[#6b7a7d]";
  const strong = dm ? "text-slate-300" : "text-[#191c1d]";
  const ff = shots.rss_feed_fixes_v1;
  const dp = shots.date_page_pass_last || shots.date_page_pass_v1;
  return (
    <div className="space-y-1 pt-1" data-testid="rs-pipe-oneshots">
      {ff && (
        <p className={`text-[11px] ${muted}`} data-testid="rs-pipe-feedfix">
          <span className={`font-medium ${strong}`}>Feed fixes</span> {ago(ff.applied_at)} · {ff.replaced?.length ?? 0} replaced · {ff.removed?.length ?? 0} removed
          {ff.probe_failed?.length ? ` (${ff.probe_failed.length} still dead after client fixes)` : ""} · feeds alive now {src.rss_alive ?? "—"}/{src.with_rss ?? "—"}
        </p>
      )}
      {dp && (
        <p className={`text-[11px] ${muted}`} data-testid="rs-pipe-pagedates">
          <span className={`font-medium ${strong}`}>Page-text date pass</span> {ago(dp.finished_at || dp.started_at)} · {dp.dated ?? 0}/{dp.candidates ?? 0} dated
          · Firecrawl scrapes {dp.firecrawl_calls ?? 0}
          {dp.by_source ? ` · ${Object.entries(dp.by_source).map(([k, v]) => `${k} ${v}`).join(", ")}` : ""}
          {dp.trigger === "manual" ? " · manual" : " · boot one-shot"}
        </p>
      )}
    </div>
  );
};

export const ReportPipelineCard = ({ dm, onChanged }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/reports/pipeline-status`);
      setStatus(r.data);
    } catch (e) {
      toast.error(errMsg(e, "Pipeline status unavailable"));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (key, url, okMsg) => {
    setBusy(key);
    try {
      const r = await axios.post(url, {}, { timeout: 300000 });
      toast.success(okMsg(r.data || {}));
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(errMsg(e, `${key} failed`));
    } finally {
      setBusy(null);
    }
  };

  const rep = status?.reports || {};
  const src = status?.sources || {};
  const runs = status?.last_runs || {};
  const btn = (dm
    ? "text-slate-300 hover:text-cyan-300 hover:bg-white/5"
    : "text-[#3d4a4d] hover:text-[#006874] hover:bg-[#f3f4f5]");

  return (
    <div className={`rounded-2xl p-5 ${dm ? "bg-[#1a2122]" : "bg-white"}`}
         style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}
         data-testid="rs-pipeline-card">
      <div className="flex items-start gap-3 mb-4">
        <div className={`mt-0.5 w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center ${dm ? "bg-cyan-400/10 text-cyan-400" : "bg-[#006874]/8 text-[#006874]"}`}>
          <Activity className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`text-sm font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Discovery pipeline</h3>
          <p className={`text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            Weekly Firecrawl sweep (60% Alta / 40% rest, least-recently-scanned first) · daily RSS poll · weekly Report Radar · metadata enrichment every 6h.
          </p>
        </div>
        <button onClick={load} className={`p-1.5 rounded-md ${btn}`} aria-label="Refresh" data-testid="rs-pipeline-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 mb-4">
        <Stat dm={dm} label="Published" value={rep.published} testid="rs-pipe-published" />
        <Stat dm={dm} label="Added 7d" value={rep.added_7d} testid="rs-pipe-added7d" />
        <Stat dm={dm} label="Added 30d" value={rep.added_30d} testid="rs-pipe-added30d" />
        <Stat dm={dm} label="Undated" value={rep.undated} warn={rep.undated > 10} testid="rs-pipe-undated" />
        <Stat dm={dm} label="Hidden by gates" value={rep.hidden_by_quality_gates} testid="rs-pipe-hidden" />
        <Stat dm={dm} label="Hubs never scanned" value={`${src.hub_never_scanned ?? "—"} / ${src.with_hub ?? "—"}`} warn={src.hub_never_scanned > 0} testid="rs-pipe-neverscanned" />
        <Stat dm={dm} label="Radar sources" value={src.created_by_radar} testid="rs-pipe-radar-sources" />
      </div>

      <div className="flex flex-col lg:flex-row lg:items-end gap-4">
        <div className="flex-1 space-y-1">
          <RunLine dm={dm} label="Weekly sweep" run={runs.weekly_sweep} testid="rs-pipe-run-sweep" />
          <RunLine dm={dm} label="RSS poll" run={runs.rss} testid="rs-pipe-run-rss" />
          <RunLine dm={dm} label="Report Radar" run={runs.radar} testid="rs-pipe-run-radar" />
          {rep.added_30d_by_path && (
            <p className={`text-[11px] ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`} data-testid="rs-pipe-by-path">
              30d by path: {Object.entries(rep.added_30d_by_path).map(([k, v]) => `${k} ${v}`).join(" · ") || "—"}
            </p>
          )}
          <OneShots dm={dm} shots={status?.one_shots} src={src} />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button disabled={!!busy} onClick={() => run("rss", `${API}/admin/reports/rss-poll`, (d) => `RSS: ${d.reports_inserted ?? 0} new from ${d.sources_scanned ?? 0} feeds (${d.feeds_failed ?? 0} feed errors)`)}
                  className={`flex items-center gap-1 category-tag px-2.5 py-1.5 rounded-md disabled:opacity-40 ${btn}`} data-testid="rs-pipe-run-rss-btn">
            {busy === "rss" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Rss className="w-3.5 h-3.5" />} Poll RSS now
          </button>
          <button disabled={!!busy} onClick={() => run("radar", `${API}/admin/reports/radar`, (d) => `Radar: ${d.reports_inserted ?? 0} new report(s), ${(d.sources_created || []).length} new source(s) from ${d.candidates_found ?? 0} candidates`)}
                  className={`flex items-center gap-1 category-tag px-2.5 py-1.5 rounded-md disabled:opacity-40 ${btn}`} data-testid="rs-pipe-run-radar-btn">
            {busy === "radar" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Radar className="w-3.5 h-3.5" />} Run Radar now
          </button>
          <button disabled={!!busy} onClick={() => run("dates", `${API}/admin/reports/backfill-dates?max_reports=100&wait=true`, (d) => `Dates: ${d.dated ?? 0} dated, ${d.hidden_by_classifier ?? 0} hidden as non-research (${d.candidates ?? 0} checked)`)}
                  className={`flex items-center gap-1 category-tag px-2.5 py-1.5 rounded-md disabled:opacity-40 ${btn}`} data-testid="rs-pipe-backfill-dates-btn">
            {busy === "dates" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CalendarClock className="w-3.5 h-3.5" />} Backfill dates
          </button>
          <button disabled={!!busy} onClick={() => run("pagedates", `${API}/admin/reports/backfill-dates-page?firecrawl_budget=0&max_reports=60&retry_attempted=true`, (d) => `Page-text pass: ${d.dated ?? 0} dated of ${d.candidates ?? 0} (free fetch only, 0 Firecrawl)`)}
                  className={`flex items-center gap-1 category-tag px-2.5 py-1.5 rounded-md disabled:opacity-40 ${btn}`} data-testid="rs-pipe-page-dates-btn">
            {busy === "pagedates" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FileSearch className="w-3.5 h-3.5" />} Page-text dates (free)
          </button>
          <button disabled={!!busy} onClick={() => run("junk", `${API}/admin/reports/cleanup-junk`, (d) => `Cleanup: ${d.hidden ?? 0} junk/duplicate report(s) hidden`)}
                  className={`flex items-center gap-1 category-tag px-2.5 py-1.5 rounded-md disabled:opacity-40 ${btn}`} data-testid="rs-pipe-cleanup-btn">
            {busy === "junk" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Eraser className="w-3.5 h-3.5" />} Clean junk
          </button>
        </div>
      </div>
      <HiddenReportsList dm={dm} onRestored={() => { load(); onChanged && onChanged(); }} />
    </div>
  );
};

export default ReportPipelineCard;
